import { useCallback, useEffect, useRef, useState } from "react";
import { isHouseJob, isMasterJob, JobNames } from "../framework.core/JobNames";

/**
 * 액션 완료 토스트 (v2).
 *
 * - 우측 상단 고정, 5초 후 자동 소멸 (슬라이드 인 / 페이드 아웃 애니메이션)
 * - 정상 완료 ✓ / 실패 ✕ 아이콘으로 구분
 * - × 버튼: 즉시 닫기 (이동 없음)
 * - 본문 클릭: 액션 결과의 link({ type, id })를 buildEntityUrl()로 URL 변환해 이동
 *
 * 백엔드 documentCompleted 이벤트의 document.actionResult 권장 규격(camelCase):
 * {
 *   actionType: "BillOfLading",
 *   message: "Ocean Import House B/L (HBL No: SEL1234567) has been created.",
 *   entity: { kind, blType, refNo, mblNo, mblId, hblNo, hblId },
 *   link: { type: "hbl", id: 5678 }
 * }
 */

const TOAST_TTL_MS = 30_000;
const LEAVE_MS = 220;

interface Toast {
  id: string;
  message: string;
  leaving: boolean;
  jobKey?: string | null;
  onClick?: () => void;   // 지정되면 본문 클릭 시 onNavigate 대신 실행 (예: ISF → ISF List 필터 이동)
}

/**
 * 액션 결과의 link를 실제 화면 URL로 바꾼다.
 * TODO: 실제 운영 프론트엔드의 라우팅 규칙에 맞게 채워 주세요.
 *   예) case "hbl": return `${SOLUTION_BASE_URL}/ocean/import/hbl/${link.id}`;
 * null을 반환하면 ToastContainer가 이동 대신 안내(alert)만 표시합니다.
 */
/** actionResult.entity로 영어 문장을 조립한다 (message가 비어 있을 때의 폴백). */
function entitySentence(document: any) {
  const entity = document.actionResult.meta;
  if (!entity) return null;
    let prefix = "";
    switch(entity.jobName) {
        case JobNames.OIMbl:
        case JobNames.OIHbl:
           prefix = 'Ocean Import';
           break;
        case JobNames.OEMbl:
        case JobNames.OEHbl:
           prefix = 'Ocean Export';
           break;
        case JobNames.AIMbl:
        case JobNames.AIHbl:
           prefix = 'Air Import';
           break;        
        case JobNames.AEMbl:
        case JobNames.AEHbl:
           prefix = 'Air Export';
          break;
        case JobNames.Invoice:
           prefix = 'Invoice';
           break;
        case JobNames.Bill:
           prefix = 'Bill';
           break;
        case JobNames.CrDb:
           prefix = 'CrDb';
           break;
        case JobNames.ISF:
           prefix = 'ISF';
           break;            
        default:
           prefix = '';
    }
    
  // 기존 건 갱신 결과 (2026-09-23): created / updated (N fields) / already up to date / needs review
  const outcome = entity.review
    ? ` needs review: ${entity.review}`
    : entity.updated
      ? ` has been updated (${Array.isArray(entity.changes) ? entity.changes.length : 0} field(s)).`
      : entity.existing
        ? " already exists - no changes."
        : " has been created.";
  if (isHouseJob(entity.jobName) && entity.hblNo)
    return `${prefix} House B/L (HBL No: ${entity.hblNo})${outcome}`;
  if (isMasterJob(entity.jobName) && entity.mblNo)
    return `${prefix} Master B/L (MBL No: ${entity.mblNo})${outcome}`;
  if([JobNames.Invoice, JobNames.Bill, JobNames.CrDb].includes(entity.jobName)){
    return `${prefix} (${prefix} No: ${entity.invoiceNo})${outcome}`;
  }
  if(JobNames.ISF === entity.jobName) {
    if(!entity.shipmentId)
      return `${prefix} 10+2 Filing has been scheduled.[${entity.isfType.replaceAll('"','')}]`;
    else
      return `${document.docType} was created with Shipment ID ${entity.shipmentId}`;
  }
  return null;
}

/** documentCompleted 이벤트의 document → 토스트 데이터. (push(buildActionToast(doc))) */
export function buildActionToast(document: any) {
  const result = document?.actionResult ?? null;
  const ok = document?.status === "Completed";
  const title = document?.docTypeName || document?.docType || "Document";

  const body = ok
    ? document.message ||
      entitySentence(document) ||
      `${title} has been processed.`
    : document.message ||
      `${title} failed${document?.error ? `: ${document.error}` : "."}`;
      
  return {
    kind: ok ? "success" : "error",
    title: ok ? `${title} — Done` : `${title} — Failed`,
    body,
    entity: ok ? result?.meta ?? null : null,

  };
}

/**
 * 업로드가 409(중복)로 막혔을 때의 안내 토스트 (2026-09-22). 서버 DuplicateResponse →
 * 앰버 "Already processed" / "Already in progress". 클릭하면 이전 처리에서 생성된 첫 레코드로 이동한다
 * (entity 키는 AI Center 목록과 같은 jobName/jobId/parentJobName/parentJobId).
 */
export function buildDuplicateToast(dup: any) {
  const docs: any[] = dup?.original?.documents ?? [];
  // 살아 있는 레코드만 링크한다 (recordExists === false 는 유저가 지운 것)
  const first = docs.find((d) => d.status === "Completed" && d.jobId > 0 && d.jobName && d.recordExists !== false);
  return {
    kind: "warn",
    title: dup?.resumed ? "Resuming earlier job" : dup?.inProgress ? "Already in progress" : "Already processed",
    body: dup?.message ?? "This file was already processed.",
    entity: first
      ? { jobName: first.jobName, jobId: first.jobId, parentJobName: first.parentJobName,
          parentJobId: first.parentJobId, isfType: first.isfType, shipmentId: first.shipmentId }
      : null,
  };
}

/** 409 응답에서 "살아 있는 레코드가 있고 링크가 되는" 문서만 — 토스트·카드 링크 대상. */
export function processedRecords(dup: any): any[] {
  const docs: any[] = dup?.original?.documents ?? [];
  return docs.filter((d) => d.status === "Completed" && d.jobId > 0 && d.jobName && d.recordExists !== false);
}

/**
 * 이미 처리된 레코드 하나 → 토스트 하나 (2026-09-23 hykim: 동시에 3~4개면 한꺼번에 다 띄우는 게 명확).
 * 같은 드롭의 토스트는 같은 jobKey를 써서 서로를 닫지 않게 한다. 클릭하면 그 레코드로 이동.
 */
export function buildProcessedRecordToast(
  d: any,
  meta?: { source?: string | null; droppedBy?: string | null; createdAt?: string | null }
) {
  const cat = d.docTypeName || d.docType || "Document";
  const src = meta?.source === "INTAKE" ? "Email Intake" : meta?.source === "DROP" ? "AI Drop Zone" : "";
  const who = meta?.droppedBy ? ` by ${meta.droppedBy}` : "";
  const when = meta?.createdAt ? new Date(meta.createdAt).toLocaleString() : "";
  return {
    kind: "warn",
    title: `${cat} — Already processed`,
    body: `${cat}${d.actionNo ? ` ${d.actionNo}` : ""} was created earlier` +
      `${src ? ` through ${src}${who}` : ""}${when ? ` (${when})` : ""}.`,
    entity: d.jobId > 0 && d.jobName
      ? { jobName: d.jobName, jobId: d.jobId, parentJobName: d.parentJobName,
          parentJobId: d.parentJobId, isfType: d.isfType, shipmentId: d.shipmentId }
      : null,
  };
}

/** 토스트 목록 상태 + push/dismiss. 퇴장 애니메이션 후 실제 제거된다. */
export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef(new Map()); // id -> timeout[]
  const toastsRef = useRef<Toast[]>([]);

    useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t:any) => t.id !== id));
    (timersRef.current.get(id) ?? []).forEach(clearTimeout);
    timersRef.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id) => {
      setToasts((prev:any) => prev.map((t: any) => (t.id === id ? { ...t, leaving: true } : t)));
      const t = setTimeout(() => remove(id), LEAVE_MS);
      timersRef.current.set(id, [...(timersRef.current.get(id) ?? []), t]);
    },
    [remove]
  );

  const push = useCallback(
    (toast) => {
      //const id = toast.id ?? crypto.randomUUID();
      const id = toast.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const jobKey = toast.jobKey ?? null;

      // 새 메세지가 도착 하면 이 전에 떠 있는 메세지는 즉시 닫음 
      if (jobKey != null) {
        toastsRef.current
          .filter((t: any) => t.jobKey !== jobKey && !t.leaving)
          .forEach((t: any) => dismiss(t.id));
      }

      setToasts((prev: Toast[]) => [...prev, { ...toast, id, leaving: false }]);
      const t = setTimeout(() => dismiss(id), toast.ttlMs ?? TOAST_TTL_MS);
      timersRef.current.set(id, [t]);
      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach((list) => list.forEach(clearTimeout)); // 언마운트 정리
  }, []);

  return { toasts, push, dismiss };
}

export function ToastContainer({ toasts, onDismiss, onNavigate }:{toasts : any, onDismiss: any, onNavigate: any}) {

  if (toasts.length === 0) return null;

  return (
    <div className="aitoast">
      <style>{STYLE}</style>
      {toasts.map((t : any) => (
        <div
          key={t.id}
          role="status"
          className={`aitoast-item ${t.kind}${t.leaving ? " leaving" : ""}${t.entity?.jobId || t.onClick ? " link" : ""}`}
          onClick={() => (t.onClick ? t.onClick() : onNavigate(t.entity))}
        >
          <span className={`aitoast-ic ${t.kind}`} aria-hidden="true">
            {t.kind === "error" ? "✕" : t.kind === "warn" ? "!" : "✓"}
          </span>
          <div className="aitoast-body">
            <div className="aitoast-title">{t.title}</div>
            <div className="aitoast-msg">{t.body}</div>
            {!!t.entity?.jobId && (t.entity.jobName !== JobNames.ISF || t.onClick) && (
              <div className="aitoast-go">
                {t.kind === "warn" ? "Click to open the record created earlier →" : "Click to open the created record →"}
              </div>
            )}
          </div>
          <button
            className="aitoast-x"
            aria-label="close"
            onClick={(e) => {
              e.stopPropagation(); // × 클릭이 본문 클릭(이동)으로 번지지 않게
              onDismiss(t.id);
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/* my-design-system 토큰 기반 스코프 스타일 */
const STYLE = `
.aitoast {
  --surface:#FFFFFF; --border:#E2E8F0;
  --text:#0F172A; --text-2:#475569; --text-3:#94A3B8;
  --accent:#2563EB;
  --success:#16A34A; --success-subtle:#F0FDF4;
  --warning:#D97706; --warning-subtle:#FFFBEB;
  --danger:#DC2626;  --danger-subtle:#FEF2F2;
  position:fixed; top:140px; right:16px; z-index:1000;
  display:flex; flex-direction:column; gap:8px;
  width:340px; max-width:calc(100vw - 32px);
  font-family:"Pretendard Variable",Pretendard,-apple-system,"Segoe UI","Noto Sans KR",sans-serif;
}
.aitoast-item {
  display:flex; gap:10px; align-items:flex-start;
  background:var(--surface); border:1px solid var(--border); border-radius:8px;
  padding:10px 12px; box-shadow:0 4px 12px rgb(15 23 42 / .12);
  animation:aitoast-in .2s ease-out;
  transition:opacity .2s ease-out, transform .2s ease-out;
}
.aitoast-item.link { cursor:pointer; }
.aitoast-item.link:hover { transform:translateY(-1px); }
.aitoast-item.leaving { opacity:0; transform:translateX(12px); }
@keyframes aitoast-in {
  from { opacity:0; transform:translateX(12px); }
  to   { opacity:1; transform:translateX(0); }
}
.aitoast-ic {
  flex:none; width:20px; height:20px; border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  font-size:12px; font-weight:700; margin-top:1px;
}
.aitoast-ic.success { color:var(--success); background:var(--success-subtle); }
.aitoast-ic.warn    { color:var(--warning); background:var(--warning-subtle); }
.aitoast-ic.error   { color:var(--danger);  background:var(--danger-subtle); }
.aitoast-item.warn  { border-color:#FDE68A; }
.aitoast-body { flex:1 1 auto; min-width:0; }
.aitoast-title { font-size:13px; font-weight:600; color:var(--text); }
.aitoast-msg { font-size:12.5px; color:var(--text-2); margin-top:2px; line-height:1.45; }
.aitoast-go { font-size:11px; color:var(--accent); margin-top:5px; }
.aitoast-x {
  flex:none; border:none; background:transparent; cursor:pointer;
  color:var(--text-3); font-size:16px; line-height:1; padding:0 2px; border-radius:4px;
}
.aitoast-x:hover { color:var(--text); }
@media (prefers-reduced-motion: reduce) {
  .aitoast-item { animation:none; transition:none; }
}
`;
